import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, Download } from "lucide-react";
import { fetchTenantMonthlyAudit, type TenantMonthlyAudit } from "../../api/client";
import type { Tenant } from "../../api/types";
import { fmtCurrency, fmtCurrencyAcct, fmtPSF, fmtPercent, fmtSF, fmtDateShort } from "../../utils/format";
import { downloadCSV } from "../../utils/csv";
import LoadingSpinner from "../LoadingSpinner";

// ---------- types ----------

interface RentScheduleTabProps {
  dealId: string;
  scenarioId: string;
  tenants: Tenant[];
}

interface MonthColumn {
  year: number;
  month: number;
  label: string; // "Jan 2025"
  calendarYear: number;
}

// ---------- helpers ----------

function monthYearLabel(calendarYear: number, month: number): string {
  return fmtDateShort(`${calendarYear}-${String(month).padStart(2, "0")}-01`);
}

function buildMonthColumns(rows: TenantMonthlyAudit[]): MonthColumn[] {
  if (rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => {
    if (a.calendar_year !== b.calendar_year) return a.calendar_year - b.calendar_year;
    return a.month - b.month;
  });
  return sorted.map((r) => ({
    year: r.year,
    month: r.month,
    label: monthYearLabel(r.calendar_year, r.month),
    calendarYear: r.calendar_year,
  }));
}

function statusLabel(row: TenantMonthlyAudit): string {
  if (row.in_downtime) return "Vacant";
  if (row.in_free_rent) return "Free Rent";
  if (row.absorption_fraction < 1 && row.absorption_fraction > 0) return "Absorption";
  if (row.occupied) return "Occupied";
  return "Vacant";
}

function statusBgClass(row: TenantMonthlyAudit): string {
  if (row.in_downtime) return "bg-red-100 text-red-800";
  if (row.in_free_rent) return "bg-honey-100 text-honey-800";
  if (row.absorption_fraction < 1 && row.absorption_fraction > 0) return "bg-blue-100 text-blue-800";
  if (row.occupied) return "bg-green-100 text-green-800";
  return "bg-red-100 text-red-800";
}

function yearGroupHeaders(columns: MonthColumn[]): { calendarYear: number; span: number }[] {
  const groups: { calendarYear: number; span: number }[] = [];
  for (const col of columns) {
    const last = groups[groups.length - 1];
    if (last && last.calendarYear === col.calendarYear) {
      last.span++;
    } else {
      groups.push({ calendarYear: col.calendarYear, span: 1 });
    }
  }
  return groups;
}

// ---------- Desktop matrix ----------

interface MatrixProps {
  rows: TenantMonthlyAudit[];
  columns: MonthColumn[];
}

const ROW_DEFS: { key: string; label: string; render: (r: TenantMonthlyAudit) => string | null }[] = [
  { key: "status", label: "Status", render: (r) => statusLabel(r) },
  { key: "absorption", label: "Absorption %", render: (r) => r.absorption_fraction < 1 ? fmtPercent(r.absorption_fraction) : null },
  { key: "contract_rent", label: "Contract Rent PSF", render: (r) => fmtPSF(r.contract_rent_psf) },
  { key: "potential_rent", label: "Potential Rent", render: (r) => fmtCurrencyAcct(r.potential_rent) },
  { key: "collected_rent", label: "Collected Rent", render: (r) => fmtCurrencyAcct(r.collected_rent) },
  { key: "variance", label: "Variance", render: (r) => { const v = r.potential_rent - r.collected_rent; return v > 0.5 ? fmtCurrencyAcct(v) : null; } },
  { key: "reimbursement", label: "Reimbursement", render: (r) => fmtCurrencyAcct(r.monthly_reimbursement) },
  { key: "ti", label: "TI", render: (r) => r.ti_this_month > 0 ? fmtCurrencyAcct(r.ti_this_month) : null },
  { key: "lc", label: "LC", render: (r) => r.lc_this_month > 0 ? fmtCurrencyAcct(r.lc_this_month) : null },
  { key: "net_cf", label: "Net Tenant CF", render: (r) => fmtCurrencyAcct(r.collected_rent + r.monthly_reimbursement - r.ti_this_month - r.lc_this_month) },
  { key: "events", label: "Events", render: (r) => r.events_this_month.length > 0 ? r.events_this_month.join(", ") : null },
];

const SW = "w-[180px] min-w-[180px]";
const CW = "w-[90px] min-w-[90px]";

function getTooltipText(key: string, d: TenantMonthlyAudit): string {
  if (key === "collected_rent" || key === "potential_rent") return d.collection_explanation;
  if (key === "reimbursement") return d.reimbursement_explanation;
  if (key === "ti" || key === "lc") return d.ti_lc_explanation || "";
  if (key === "contract_rent" && d.rent_step_description) return d.rent_step_description;
  return "";
}

function DesktopMatrix({ rows, columns }: MatrixProps) {
  const yearGroups = useMemo(() => yearGroupHeaders(columns), [columns]);
  const byMonth = useMemo(() => {
    const m = new Map<string, TenantMonthlyAudit>();
    for (const r of rows) m.set(`${r.calendar_year}-${r.month}`, r);
    return m;
  }, [rows]);
  const [tip, setTip] = useState<{ row: string; col: string; text: string } | null>(null);

  return (
    <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="border-collapse">
          <thead>
            <tr className="bg-grove-100 border-b border-grove-300">
              <th className={`${SW} sticky left-0 z-10 bg-grove-100 px-3 py-2`} />
              {yearGroups.map((g) => (
                <th key={g.calendarYear} colSpan={g.span} className="px-1 py-1.5 text-[11px] font-bold text-bark-600 text-center border-l border-grove-300">{g.calendarYear}</th>
              ))}
            </tr>
            <tr className="bg-grove-50 border-b border-grove-300">
              <th className={`${SW} sticky left-0 z-10 bg-grove-50 px-3 py-2 text-left text-[11px] font-semibold text-bark-400 uppercase tracking-wider`}>Metric</th>
              {columns.map((c) => (
                <th key={c.label} className={`${CW} px-1 py-2 text-[11px] font-semibold text-bark-400 text-right whitespace-nowrap border-l border-grove-200`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROW_DEFS.map((rd) => (
              <tr key={rd.key} className="border-b border-grove-200 last:border-0">
                <td className={`${SW} sticky left-0 z-10 bg-white px-3 py-1.5 text-[11px] font-medium text-bark-600 whitespace-nowrap border-r border-grove-200`}>{rd.label}</td>
                {columns.map((col) => {
                  const isNumericRow = rd.key !== "status" && rd.key !== "events";
                  const alignCls = isNumericRow ? "text-right whitespace-nowrap" : "text-center";
                  const md = byMonth.get(`${col.calendarYear}-${col.month}`);
                  if (!md) return <td key={col.label} className={`${CW} px-1 py-1.5 ${alignCls} text-[11px] text-bark-300 border-l border-grove-100`}>{"\u2014"}</td>;
                  const val = rd.render(md);
                  const ttText = getTooltipText(rd.key, md);
                  const show = tip?.row === rd.key && tip?.col === col.label;
                  return (
                    <td key={`${rd.key}-${col.label}`}
                      className={`${CW} px-1 py-1.5 ${alignCls} text-[11px] tabular-nums border-l border-grove-100 relative cursor-default ${rd.key === "status" ? statusBgClass(md) : ""} ${rd.key === "events" ? "text-bark-500 text-[10px]" : "text-bark-700"}`}
                      onClick={() => ttText && setTip(show ? null : { row: rd.key, col: col.label, text: ttText })}
                      onMouseLeave={() => show && setTip(null)}>
                      {rd.key === "status" ? <span className="text-[10px] font-semibold">{val}</span> : val || "\u2014"}
                      {show && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-20 w-56 px-3 py-2 bg-bark-800 text-white text-[10px] rounded-lg shadow-lg pointer-events-none">{tip.text}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Mobile cards ----------

function MobileCards({ rows }: { rows: TenantMonthlyAudit[] }) {
  const sorted = useMemo(() =>
    [...rows].sort((a, b) => a.calendar_year !== b.calendar_year ? a.calendar_year - b.calendar_year : a.month - b.month), [rows]);
  const Kv = ({ l, v, bold }: { l: string; v: string; bold?: boolean }) => (
    <div className="flex justify-between">
      <span className="text-bark-400">{l}</span>
      <span className={`tabular-nums ${bold ? "font-medium text-bark-800" : "text-bark-700"}`}>{v}</span>
    </div>
  );
  return (
    <div className="space-y-3">
      {sorted.map((r) => (
        <div key={`${r.calendar_year}-${r.month}`} className="bg-white border border-grove-300 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-bark-700">{monthYearLabel(r.calendar_year, r.month)}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBgClass(r)}`}>{statusLabel(r)}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Kv l="Rent PSF" v={fmtPSF(r.contract_rent_psf)} />
            <Kv l="Collected" v={fmtCurrency(r.collected_rent)} />
            <Kv l="Reimb." v={fmtCurrency(r.monthly_reimbursement)} />
            <Kv l="Net CF" v={fmtCurrency(r.collected_rent + r.monthly_reimbursement - r.ti_this_month - r.lc_this_month)} bold />
            {r.events_this_month.length > 0 && (
              <div className="col-span-2 mt-1 flex flex-wrap gap-1">
                {r.events_this_month.map((e, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-grove-100 text-bark-600 rounded text-[10px]">{e}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Summary card ----------

const sumLbl = "text-bark-400 uppercase tracking-wider text-[11px] font-medium";

function TenantSummaryCard({ rows, tenant }: { rows: TenantMonthlyAudit[]; tenant: Tenant }) {
  const t = useMemo(() => {
    let rent = 0, reimb = 0, ti = 0, lc = 0;
    for (const r of rows) { rent += r.collected_rent; reimb += r.monthly_reimbursement; ti += r.ti_this_month; lc += r.lc_this_month; }
    return { rent, reimb, ti, lc, netCF: rent + reimb - ti - lc };
  }, [rows]);

  return (
    <div className="bg-white border border-grove-300 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-bark-700 mb-3">
        {tenant.tenant_name}
        {tenant.suite_id && <span className="text-bark-400 font-normal ml-2">Suite {tenant.suite_id}</span>}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
        <div><span className={sumLbl}>SF</span><p className="text-bark-700 tabular-nums mt-0.5">{fmtSF(tenant.leased_sf)}</p></div>
        <div><span className={sumLbl}>Lease Start</span><p className="text-bark-700 mt-0.5">{fmtDateShort(tenant.lease_start_date)}</p></div>
        <div><span className={sumLbl}>Lease End</span><p className="text-bark-700 mt-0.5">{fmtDateShort(tenant.lease_expiration_date)}</p></div>
        <div><span className={sumLbl}>Total Rent</span><p className="text-bark-700 tabular-nums font-medium mt-0.5">{fmtCurrency(t.rent)}</p></div>
        <div><span className={sumLbl}>Total Reimb.</span><p className="text-bark-700 tabular-nums mt-0.5">{fmtCurrency(t.reimb)}</p></div>
        <div><span className={sumLbl}>Total TI / LC</span><p className="text-bark-700 tabular-nums mt-0.5">{fmtCurrency(t.ti)} / {fmtCurrency(t.lc)}</p></div>
        <div className="col-span-2 sm:col-span-3 pt-2 border-t border-grove-200">
          <span className={sumLbl}>Net Tenant Cash Flow (Hold Period)</span>
          <p className="text-lg font-semibold text-bark-800 tabular-nums mt-0.5">{fmtCurrency(t.netCF)}</p>
        </div>
      </div>
    </div>
  );
}

// ---------- Main component ----------

export default function RentScheduleTab({ dealId, scenarioId, tenants }: RentScheduleTabProps) {
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenants[0]?.tenant_id || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TenantMonthlyAudit[]>([]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === selectedTenantId),
    [tenants, selectedTenantId],
  );

  const loadData = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTenantMonthlyAudit(dealId, scenarioId, tenantId);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load rent schedule audit");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId]);

  useEffect(() => {
    if (selectedTenantId) loadData(selectedTenantId);
  }, [selectedTenantId, loadData]);

  const columns = useMemo(() => buildMonthColumns(data), [data]);

  if (tenants.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">No tenants found for this deal.</p>
      </div>
    );
  }

  function exportRentScheduleCSV() {
    if (data.length === 0) return;
    const headers: (string | number)[] = ["Metric", ...columns.map((c) => c.label)];
    const rows: (string | number)[][] = ROW_DEFS.map((rd) => {
      const byMonth = new Map<string, TenantMonthlyAudit>();
      for (const r of data) byMonth.set(`${r.calendar_year}-${r.month}`, r);
      return [
        rd.label,
        ...columns.map((col) => {
          const md = byMonth.get(`${col.calendarYear}-${col.month}`);
          if (!md) return "";
          return rd.render(md) || "";
        }),
      ];
    });
    const tenantName = selectedTenant?.tenant_name || "tenant";
    downloadCSV([headers, ...rows], `audit_rent_schedule_${tenantName}.csv`);
  }

  return (
    <div className="space-y-6">
      {/* Tenant selector + CSV button */}
      <div className="flex items-center gap-3">
        <label htmlFor="tenant-select" className="text-sm font-medium text-bark-600">
          Tenant
        </label>
        <select
          id="tenant-select"
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          className="flex-1 max-w-md px-3 py-2 text-sm border border-grove-300 rounded-lg bg-white text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
        >
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.tenant_name}
              {t.suite_id ? ` (${t.suite_id})` : ""}
              {" \u2014 "}
              {fmtSF(t.leased_sf)}
            </option>
          ))}
        </select>
        <button
          onClick={exportRentScheduleCSV}
          disabled={data.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all disabled:opacity-50 print:hidden"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : data.length === 0 ? (
        <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
          <p className="text-sm text-bark-400">No monthly data available for this tenant.</p>
        </div>
      ) : (
        <>
          {/* Desktop matrix (hidden on small screens) */}
          <div className="hidden sm:block">
            <DesktopMatrix rows={data} columns={columns} />
          </div>

          {/* Mobile cards (visible on small screens only) */}
          <div className="sm:hidden">
            <MobileCards rows={data} />
          </div>

          {/* Summary card */}
          {selectedTenant && <TenantSummaryCard rows={data} tenant={selectedTenant} />}
        </>
      )}
    </div>
  );
}
